import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { withTranslation } from "react-i18next";
import { FaEdit, FaMapMarkedAlt, FaMapMarkerAlt, FaTrashAlt } from "react-icons/fa";
import { toast } from "react-toastify";
import MDSpinner from "react-md-spinner";

import BackButton from "../../components/BackButton";
import FarmMapDeleteConfirmModal from "../FarmMap/FarmMapDeleteConfirmModal";
import {
  deleteGroupFarm,
  fetchGroupFarms,
  updateGroupFarm,
} from "../PendingSchedules/serviceOrderLocationApi";
import "./GroupFarmsList.css";

function GroupFarmsList({ t }) {
  const { farm_id: contextFarmId } = useParams();
  const [farms, setFarms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingFarmId, setEditingFarmId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [savingFarmId, setSavingFarmId] = useState(null);
  const [deletingFarmId, setDeletingFarmId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const loadFarms = useCallback(async () => {
    setLoading(true);
    try {
      setFarms(await fetchGroupFarms(contextFarmId));
    } catch (error) {
      toast.error(t("Não foi possível carregar as fazendas."), {
        position: "bottom-right",
      });
      setFarms([]);
    } finally {
      setLoading(false);
    }
  }, [contextFarmId, t]);

  useEffect(() => {
    document.title = "Agriconnected - Fazendas";
    loadFarms();
  }, [loadFarms]);

  const serviceFarms = useMemo(
    () => farms.filter((farm) => farm.parent),
    [farms]
  );

  function startEditing(farm) {
    setEditingFarmId(farm.id);
    setEditingName(farm.nome_da_fazenda || "");
  }

  function cancelEditing() {
    setEditingFarmId(null);
    setEditingName("");
  }

  async function saveEditing(farmId) {
    const trimmedName = editingName.trim();
    if (!trimmedName) {
      toast.error(t("Informe o nome da fazenda."), { position: "bottom-right" });
      return;
    }

    setSavingFarmId(farmId);
    try {
      await updateGroupFarm(contextFarmId, farmId, trimmedName);
      await loadFarms();
      cancelEditing();
      toast.success(t("Sub-fazenda atualizada."), { position: "bottom-right" });
    } catch (error) {
      const detail =
        error.response &&
        error.response.data &&
        error.response.data.nome_da_fazenda &&
        error.response.data.nome_da_fazenda[0];
      toast.error(detail || t("Não foi possível atualizar a sub-fazenda."), {
        position: "bottom-right",
      });
    } finally {
      setSavingFarmId(null);
    }
  }

  function requestDelete(farm) {
    setDeleteConfirm({
      id: farm.id,
      label: farm.nome_da_fazenda || t("Sub-fazenda"),
    });
  }

  async function confirmDelete() {
    if (!deleteConfirm) return;

    setDeletingFarmId(deleteConfirm.id);
    try {
      await deleteGroupFarm(contextFarmId, deleteConfirm.id);
      if (String(editingFarmId) === String(deleteConfirm.id)) {
        cancelEditing();
      }
      await loadFarms();
      toast.success(t("Sub-fazenda excluída."), { position: "bottom-right" });
      setDeleteConfirm(null);
    } catch (error) {
      toast.error(t("Não foi possível excluir a sub-fazenda."), {
        position: "bottom-right",
      });
    } finally {
      setDeletingFarmId(null);
    }
  }

  function handleEditKeyDown(event, farmId) {
    if (event.key === "Enter") {
      event.preventDefault();
      saveEditing(farmId);
    }
    if (event.key === "Escape") {
      event.preventDefault();
      cancelEditing();
    }
  }

  return (
    <div>
      <div className="x_title">
        <BackButton to={`/${contextFarmId}`} label={t("Voltar")} />
        <h2>
          <FaMapMarkerAlt style={{ marginRight: 8 }} />
          {t("Sub-fazendas")}
        </h2>
        <div className="clearfix" />
      </div>

      <p className="group-farms-intro">
        {t(
          "Sub-fazendas vinculadas à fazenda gestora. Cadastre novos perímetros em Mapa da fazenda → Desenhar fazenda."
        )}
      </p>

      <Link
        to={`/${contextFarmId}/farm-map/`}
        className="btn btn-primary group-farms-map-link"
      >
        <FaMapMarkedAlt style={{ marginRight: 6 }} />
        {t("Abrir mapa da fazenda")}
      </Link>

      {loading ? (
        <div className="group-farms-loading">
          <MDSpinner />
        </div>
      ) : serviceFarms.length === 0 ? (
        <p className="group-farms-empty">
          {t("Nenhuma sub-fazenda cadastrada. Use o mapa para desenhar o perímetro.")}
        </p>
      ) : (
        <div className="table-responsive">
          <table className="table table-striped group-farms-table">
            <thead>
              <tr>
                <th>{t("Nome")}</th>
                <th>{t("Perímetro")}</th>
                <th>{t("Ações")}</th>
              </tr>
            </thead>
            <tbody>
              {serviceFarms.map((farm) => {
                const isEditing = String(editingFarmId) === String(farm.id);
                const isSaving = String(savingFarmId) === String(farm.id);
                const isDeleting = String(deletingFarmId) === String(farm.id);

                return (
                  <tr key={farm.id}>
                    <td>
                      {isEditing ? (
                        <input
                          type="text"
                          className="form-control input-sm group-farms-edit-input"
                          value={editingName}
                          onChange={(event) => setEditingName(event.target.value)}
                          onKeyDown={(event) => handleEditKeyDown(event, farm.id)}
                          disabled={isSaving}
                          autoFocus
                        />
                      ) : (
                        farm.nome_da_fazenda
                      )}
                    </td>
                    <td>
                      {farm.boundary_geojson
                        ? t("Cadastrado no mapa")
                        : t("Sem perímetro")}
                    </td>
                    <td className="group-farms-actions">
                      {isEditing ? (
                        <>
                          <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            onClick={() => saveEditing(farm.id)}
                            disabled={isSaving || !editingName.trim()}
                          >
                            {isSaving ? t("Salvando...") : t("Salvar")}
                          </button>
                          <button
                            type="button"
                            className="btn btn-default btn-sm"
                            onClick={cancelEditing}
                            disabled={isSaving}
                          >
                            {t("Cancelar")}
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            type="button"
                            className="btn btn-default btn-sm"
                            onClick={() => startEditing(farm)}
                            disabled={isDeleting || Boolean(editingFarmId)}
                            title={t("Editar nome")}
                          >
                            <FaEdit />
                          </button>
                          <button
                            type="button"
                            className="btn btn-danger btn-sm"
                            onClick={() => requestDelete(farm)}
                            disabled={isDeleting || Boolean(editingFarmId)}
                            title={t("Excluir sub-fazenda")}
                          >
                            <FaTrashAlt />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <FarmMapDeleteConfirmModal
        open={Boolean(deleteConfirm)}
        title={t("Apagar sub-fazenda")}
        message={
          deleteConfirm
            ? t(
                'Deseja realmente apagar a sub-fazenda "{{name}}"? Os talhões voltarão para a fazenda gestora. Esta ação não pode ser desfeita.',
                { name: deleteConfirm.label }
              )
            : ""
        }
        cancelLabel={t("Cancelar")}
        confirmLabel={t("Apagar")}
        loading={Boolean(deletingFarmId)}
        onCancel={() => {
          if (!deletingFarmId) {
            setDeleteConfirm(null);
          }
        }}
        onConfirm={confirmDelete}
      />
    </div>
  );
}

export default withTranslation()(GroupFarmsList);
